import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Option } from '@fluentui/react-components';
import { VerticalStackedBarChart } from '@fluentui/react-charting';
import type { IVSChartDataPoint, IVerticalStackedChartProps } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import { useGroupSpaceTypeChartStyles } from './GroupSpaceTypeChart.styles';
import type { BusinessGroupConsumption } from '@/types/reports';
import { ROOM_TYPES } from '@/mocks/reportsMockData';

const ALL_SPACE_TYPES = '__all__';

export interface GroupSpaceTypeChartProps {
  data: BusinessGroupConsumption[];
  title: string;
}

export const GroupSpaceTypeChart = ({ data, title }: GroupSpaceTypeChartProps) => {
  const styles = useGroupSpaceTypeChartStyles();
  const { t } = useTranslation('reports');
  const [selectedSpaceType, setSelectedSpaceType] = useState<string>(ALL_SPACE_TYPES);

  const top10 = useMemo(
    () => [...data].sort((a, b) => b.bookedHours - a.bookedHours).slice(0, 10),
    [data]
  );

  const chartData: IVerticalStackedChartProps[] = useMemo(() => {
    if (selectedSpaceType === ALL_SPACE_TYPES) {
      return top10.map((group) => ({
        chartData: ROOM_TYPES.map(
          (rt): IVSChartDataPoint => ({
            legend: rt.name,
            data: group.spaceTypeBreakdown[rt.name] ?? 0,
            color: rt.color,
          })
        ),
        xAxisPoint: group.name,
      }));
    }
    const color = ROOM_TYPES.find((rt) => rt.name === selectedSpaceType)?.color ?? '#888';
    return top10.map((group) => ({
      chartData: [
        {
          legend: selectedSpaceType,
          data: group.spaceTypeBreakdown[selectedSpaceType] ?? 0,
          color,
        },
      ],
      xAxisPoint: group.name,
    }));
  }, [top10, selectedSpaceType]);

  const translatedTitle = t(title);

  return (
    <WidgetContainer title={translatedTitle}>
      <div className={styles.root} role="figure" aria-label={translatedTitle}>
        <div className={styles.filterRow}>
          <Dropdown
            className={styles.dropdown}
            aria-label={t('filters.allSpaceTypes')}
            value={
              selectedSpaceType === ALL_SPACE_TYPES ? t('filters.allSpaceTypes') : selectedSpaceType
            }
            selectedOptions={[selectedSpaceType]}
            onOptionSelect={(_, d) => setSelectedSpaceType(d.optionValue ?? ALL_SPACE_TYPES)}
          >
            <Option value={ALL_SPACE_TYPES}>{t('filters.allSpaceTypes')}</Option>
            {ROOM_TYPES.map((rt) => (
              <Option key={rt.name} value={rt.name}>
                {rt.name}
              </Option>
            ))}
          </Dropdown>
        </div>
        <ChartThemeProvider>
          <VerticalStackedBarChart
            data={chartData}
            chartTitle={translatedTitle}
            yAxisTitle="h"
            barWidth={40}
            height={300}
            enableReflow={true}
          />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
};

GroupSpaceTypeChart.displayName = 'GroupSpaceTypeChart';
